import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Plus, Search, Calendar, User, Clock, 
  AlertCircle, CheckCircle, XCircle, Pause, 
  FileText, Download, X, Eye
} from 'lucide-react';

// Mock data
const mockRequests = [
  {
    id: 1,
    title: "Ice Karkade Summer Campaign Design",
    category: "Ad Campaign",
    priority: "high",
    status: "in_progress",
    requester: "Sarah Ahmed",
    assignedTo: "Mohammed Ali",
    dueDate: "2024-06-15",
    createdAt: "2024-06-01",
    description: "Design creative assets for the new Ice Karkade summer campaign",
    objective: "Increase brand awareness and drive sales for the new summer drink",
    platform: "Instagram, Facebook",
    dimensions: "1080x1080px, 1200x628px",
    attachments: ["logo.png", "brand_guidelines.pdf"],
    comments: [
      { user: "Sarah Ahmed", message: "Please include the new brand colors", time: "2 hours ago" },
      { user: "Mohammed Ali", message: "Working on the first draft", time: "1 hour ago" }
    ]
  },
  {
    id: 2,
    title: "New Branch Opening Banner",
    category: "Branch Opening",
    priority: "medium",
    status: "in_queue",
    requester: "Fatima Hassan",
    assignedTo: null,
    dueDate: "2024-06-20",
    createdAt: "2024-06-02",
    description: "Design banner for new branch opening in Riyadh",
    objective: "Announce the opening of our new location",
    platform: "Print",
    dimensions: "3m x 1m",
    attachments: ["branch_photo.jpg"],
    comments: []
  },
  {
    id: 3,
    title: "Product Packaging Redesign",
    category: "Product Packaging",
    priority: "high",
    status: "under_review",
    requester: "Ahmed Omar",
    assignedTo: "Emma Davis",
    dueDate: "2024-06-25",
    createdAt: "2024-06-03",
    description: "Redesign packaging for the new product line",
    objective: "Improve shelf appeal and brand recognition",
    platform: "Print",
    dimensions: "Various sizes",
    attachments: ["product_photos.zip", "brand_guidelines.pdf"],
    comments: [
      { user: "Emma Davis", message: "First draft ready for review", time: "3 hours ago" }
    ]
  },
  {
    id: 4,
    title: "Social Media Content Calendar",
    category: "Social Media",
    priority: "medium",
    status: "completed",
    requester: "Mike Johnson",
    assignedTo: "Alex Kim",
    dueDate: "2024-06-10",
    createdAt: "2024-05-28",
    description: "Create visual content for monthly social media calendar",
    objective: "Maintain consistent brand presence across platforms",
    platform: "Instagram, Facebook, Twitter",
    dimensions: "1080x1080px, 1200x628px, 1200x675px",
    attachments: ["content_brief.pdf", "brand_assets.zip"],
    comments: [
      { user: "Alex Kim", message: "All content delivered and approved", time: "1 day ago" }
    ]
  }
];

const Requests = ({ isDarkMode = true }) => {
  const [requests, setRequests] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  useEffect(() => {
    setRequests(mockRequests);
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case 'in_queue': return isDarkMode ? 'bg-gray-600/30 text-gray-200 border border-gray-500/30' : 'bg-gray-100 text-gray-700 border border-gray-200';
      case 'in_progress': return isDarkMode ? 'bg-blue-600/30 text-blue-200 border border-blue-500/30' : 'bg-blue-100 text-blue-700 border border-blue-200';
      case 'missing_info': return isDarkMode ? 'bg-yellow-600/30 text-yellow-200 border border-yellow-500/30' : 'bg-yellow-100 text-yellow-700 border border-yellow-200';
      case 'on_hold': return isDarkMode ? 'bg-orange-600/30 text-orange-200 border border-orange-500/30' : 'bg-orange-100 text-orange-700 border border-orange-200';
      case 'rejected': return isDarkMode ? 'bg-red-600/30 text-red-200 border border-red-500/30' : 'bg-red-100 text-red-700 border border-red-200';
      case 'under_review': return isDarkMode ? 'bg-purple-600/30 text-purple-200 border border-purple-500/30' : 'bg-purple-100 text-purple-700 border border-purple-200';
      case 'approved': return isDarkMode ? 'bg-green-600/30 text-green-200 border border-green-500/30' : 'bg-green-100 text-green-700 border border-green-200';
      case 'ready_to_publish': return isDarkMode ? 'bg-emerald-600/30 text-emerald-200 border border-emerald-500/30' : 'bg-emerald-100 text-emerald-700 border border-emerald-200';
      case 'completed': return isDarkMode ? 'bg-green-600/30 text-green-200 border border-green-500/30' : 'bg-green-100 text-green-700 border border-green-200';
      default: return isDarkMode ? 'bg-gray-600/30 text-gray-200 border border-gray-500/30' : 'bg-gray-100 text-gray-700 border border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'in_queue': return <Clock className={`h-4 w-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`} />;
      case 'in_progress': return <AlertCircle className={`h-4 w-4 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} />;
      case 'missing_info': return <XCircle className={`h-4 w-4 ${isDarkMode ? 'text-red-400' : 'text-red-600'}`} />;
      case 'on_hold': return <Pause className={`h-4 w-4 ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`} />;
      case 'rejected': return <XCircle className={`h-4 w-4 ${isDarkMode ? 'text-red-400' : 'text-red-600'}`} />;
      case 'under_review': return <Eye className={`h-4 w-4 ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`} />;
      case 'approved': return <CheckCircle className={`h-4 w-4 ${isDarkMode ? 'text-green-400' : 'text-green-600'}`} />;
      case 'ready_to_publish': return <CheckCircle className={`h-4 w-4 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`} />;
      case 'completed': return <CheckCircle className={`h-4 w-4 ${isDarkMode ? 'text-green-400' : 'text-green-600'}`} />;
      default: return <Clock className={`h-4 w-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`} />;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return isDarkMode ? 'bg-red-600/30 text-red-200 border border-red-500/30' : 'bg-red-100 text-red-700 border border-red-200';
      case 'medium': return isDarkMode ? 'bg-yellow-600/30 text-yellow-200 border border-yellow-500/30' : 'bg-yellow-100 text-yellow-700 border border-yellow-200';
      case 'low': return isDarkMode ? 'bg-green-600/30 text-green-200 border border-green-500/30' : 'bg-green-100 text-green-700 border border-green-200';
      default: return isDarkMode ? 'bg-gray-600/30 text-gray-200 border border-gray-500/30' : 'bg-gray-100 text-gray-700 border border-gray-200';
    }
  };

  const filteredRequests = requests.filter(request => {
    const matchesFilter = filter === 'all' || request.status === filter;
    const matchesSearch = request.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         request.category.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const RequestCard = ({ request }) => (
    <motion.div
      whileHover={{ scale: 1.02 }}
      className={`backdrop-blur-xl rounded-2xl p-6 shadow-lg border transition-all duration-300 cursor-pointer ${
        isDarkMode 
          ? 'bg-black/20 border-white/10 hover:shadow-xl hover:bg-black/30' 
          : 'bg-white/80 border-gray-200/50 hover:shadow-xl hover:bg-white/90'
      }`}
      onClick={() => {
        setSelectedRequest(request);
        setIsDetailModalOpen(true);
      }}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <div className="flex items-center space-x-3 mb-2">
            <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{request.title}</h3>
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(request.priority)}`}>
              {request.priority}
            </span>
          </div>
          <p className={`text-sm mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{request.category}</p>
          <p className={`text-sm line-clamp-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{request.description}</p>
        </div>
        <div className="flex items-center space-x-2">
          {getStatusIcon(request.status)}
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(request.status)}`}>
            {request.status.replace('_', ' ')}
          </span>
        </div>
      </div>

      <div className={`flex items-center justify-between text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-1">
            <User className={`h-4 w-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`} />
            <span>{request.requester}</span>
          </div>
          {request.assignedTo && (
            <div className="flex items-center space-x-1">
              <span>→</span>
              <span>{request.assignedTo}</span>
            </div>
          )}
        </div>
        <div className="flex items-center space-x-1">
          <Calendar className={`h-4 w-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`} />
          <span>{new Date(request.dueDate).toLocaleDateString()}</span>
        </div>
      </div>

      {request.attachments.length > 0 && (
        <div className="mt-3 flex items-center space-x-2">
          <FileText className={`h-4 w-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
          <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{request.attachments.length} attachments</span>
        </div>
      )}
    </motion.div>
  );

  const RequestDetailModal = ({ request, isOpen, onClose }) => {
    if (!request) return null;

    return (
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={onClose}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className={`rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto ${
                isDarkMode ? 'bg-gray-900 border border-white/10' : 'bg-white border border-gray-200'
              }`}
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{request.title}</h2>
                  <button
                    onClick={onClose}
                    className={`p-2 rounded-lg transition-colors ${
                      isDarkMode ? 'hover:bg-white/10 text-gray-400' : 'hover:bg-gray-100 text-gray-500'
                    }`}
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Left Column - Request Details */}
                  <div className="space-y-6">
                    <div>
                      <h3 className={`text-lg font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Request Details</h3>
                      <div className="space-y-3">
                        <div>
                          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Description</p>
                          <p className={isDarkMode ? 'text-gray-300' : 'text-gray-900'}>{request.description}</p>
                        </div>
                        <div>
                          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Objective</p>
                          <p className={isDarkMode ? 'text-gray-300' : 'text-gray-900'}>{request.objective}</p>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Category</p>
                            <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{request.category}</p>
                          </div>
                          <div>
                            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Platform</p>
                            <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{request.platform}</p>
                          </div>
                          <div>
                            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Dimensions</p>
                            <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{request.dimensions}</p>
                          </div>
                          <div>
                            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Due Date</p>
                            <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{new Date(request.dueDate).toLocaleDateString()}</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h3 className={`text-lg font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Status & Priority</h3>
                      <div className="flex space-x-3">
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(request.status)}`}>
                          {request.status.replace('_', ' ')}
                        </span>
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${getPriorityColor(request.priority)}`}>
                          {request.priority} Priority
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Right Column - Comments & Attachments */}
                  <div className="space-y-6">
                    <div>
                      <h3 className={`text-lg font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Comments</h3>
                      <div className="space-y-3 max-h-48 overflow-y-auto">
                        {request.comments.length > 0 ? (
                          request.comments.map((comment, index) => (
                            <div key={index} className={`rounded-lg p-3 ${
                              isDarkMode ? 'bg-black/20 border border-white/10' : 'bg-gray-50'
                            }`}>
                              <div className="flex items-center justify-between mb-1">
                                <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{comment.user}</span>
                                <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>{comment.time}</span>
                              </div>
                              <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{comment.message}</p>
                            </div>
                          ))
                        ) : (
                          <p className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>No comments yet</p>
                        )}
                      </div>
                    </div>

                    <div>
                      <h3 className={`text-lg font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Attachments</h3>
                      <div className="space-y-2">
                        {request.attachments.map((attachment, index) => (
                          <div key={index} className={`flex items-center space-x-2 p-2 rounded-lg ${
                            isDarkMode ? 'bg-black/20 border border-white/10' : 'bg-gray-50'
                          }`}>
                            <FileText className={`h-4 w-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                            <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{attachment}</span>
                            <button className={`ml-auto p-1 rounded transition-colors ${
                              isDarkMode ? 'hover:bg-white/10' : 'hover:bg-gray-200'
                            }`}>
                              <Download className={`h-3 w-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                <div className={`flex justify-end space-x-3 mt-6 pt-6 border-t ${
                  isDarkMode ? 'border-white/10' : 'border-gray-200'
                }`}>
                  <button className={`px-4 py-2 rounded-lg transition-colors ${
                    isDarkMode 
                      ? 'text-gray-400 hover:text-white hover:bg-white/10' 
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  }`}>
                    Edit Request
                  </button>
                  <button className="px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg hover:shadow-lg transition-all duration-300">
                    Add Comment
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    );
  };

  return (
    <div className="w-full h-full px-8 py-8 space-y-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0"
      >
        <div>
          <h1 className="text-4xl font-bold bg-gradient-to-r from-white to-purple-200 bg-clip-text text-transparent">
            Design Requests
          </h1>
          <p className="text-gray-300 mt-2 text-lg">
            Manage and track design requests from across the organization
          </p>
        </div>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl font-medium hover:shadow-lg transition-all duration-300 flex items-center space-x-2"
          onClick={() => {
            // TODO: Implement create request functionality
          }}
        >
          <Plus className="h-5 w-5" />
          <span>New Request</span>
        </motion.button>
      </motion.div>

      {/* Filters and Search */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className={`backdrop-blur-xl rounded-2xl p-6 shadow-lg border ${
          isDarkMode 
            ? 'bg-black/20 border-white/10' 
            : 'bg-white/80 border-gray-200/50'
        }`}
      >
        <div className="flex flex-col sm:flex-row sm:items-center space-y-4 sm:space-y-0 sm:space-x-4">
          <div className="flex-1">
            <div className="relative">
              <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
              <input
                type="text"
                placeholder="Search requests..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={`w-full pl-10 pr-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 ${
                  isDarkMode 
                    ? 'bg-black/20 border border-white/10 text-white placeholder-gray-400' 
                    : 'bg-gray-50 border border-gray-200 text-gray-900 placeholder-gray-500'
                }`}
              />
            </div>
          </div>
          <div className="flex space-x-2">
            {['all', 'in_queue', 'in_progress', 'under_review', 'completed'].map((status) => (
              <button
                key={status}
                onClick={() => setFilter(status)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  filter === status
                    ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white'
                    : isDarkMode
                      ? 'text-gray-400 hover:text-white hover:bg-white/10'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                {status === 'all' ? 'All' : status.replace('_', ' ')}
              </button>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Requests Grid */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="grid grid-cols-1 lg:grid-cols-2 gap-6"
      >
        {filteredRequests.map((request, index) => (
          <motion.div
            key={request.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 + index * 0.1 }}
          >
            <RequestCard request={request} />
          </motion.div>
        ))}
      </motion.div>

      {/* Request Detail Modal */}
      <RequestDetailModal 
        request={selectedRequest}
        isOpen={isDetailModalOpen}
        onClose={() => {
          setIsDetailModalOpen(false);
          setSelectedRequest(null);
        }}
      />
    </div>
  );
};

export default Requests; 